import { cur } from '@/lib/currency';
import { useEffect, useState, useMemo, Fragment } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HandCoins, Plus, ArrowRight, AlertTriangle, CheckCircle2, Eye, Trash2, Link2, Receipt, Sparkles, X, Banknote, ChevronDown, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { exportToCsv } from '@/lib/exportCsv';
import { toast } from 'sonner';
import GettingStartedChecklist from '@/components/common/GettingStartedChecklist';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import BankingFlowRecap from '@/components/common/BankingFlowRecap';
import { localToday } from '@/lib/dateRange';

const fmtSR = (n) => `${cur()} ${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// iter371: 5 supported draw-down kinds — drives the kind selector +
// per-kind required-field guards in the record-downstream dialog.
const KIND_OPTIONS = [
  { value: 'expense', label: 'Expense' },
  { value: 'supplier_payment', label: 'Supplier Payment' },
  { value: 'salary_advance', label: 'Salary / Employee Advance' },
  { value: 'partner_payment', label: 'Partner Draw / Payment' },
  { value: 'loan_repayment', label: 'Loan Repayment' },
];
const KIND_LABELS = Object.fromEntries(KIND_OPTIONS.map(k => [k.value, k.label]));

/**
 * PassThroughAdvancesPage — track money handed to employees/partners
 * for spend-on-our-behalf and reconcile downstream expenses against it.
 *
 * Key UX:
 *   - Top tile: total open balance (money in personnel hands not yet
 *     accounted for). Clicking "open" filters the list.
 *   - List is sortable by status with chips + remaining-balance bar.
 *   - "+" creates a new advance via inline dialog.
 *   - Click row → detail dialog showing every linked expense + a
 *     "+ Link existing" affordance to attach a row recorded earlier.
 */
export default function PassThroughAdvancesPage() {
  const [data, setData] = useState({ items: [], count: 0, open_total: 0 });
  const [grouped, setGrouped] = useState({ groups: [], total_advance: 0, total_adjusted: 0, open_remaining: 0 });
  const [showGrouped, setShowGrouped] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailFor, setDetailFor] = useState(null); // {advance, expenses, sup_payments}
  const [people, setPeople] = useState({ employees: [], partners: [], suppliers: [], customers: [] });
  const [bankAccounts, setBankAccounts] = useState([]);
  const [branches, setBranches] = useState([]);
  // iter395 — Suggested advances from un-linked bank-payments to
  // employees/partners. Offers "+ Advance" one-click conversion so
  // users don't have to retype data already on the bank line.
  const [suggested, setSuggested] = useState([]);
  const [suggestedLoading, setSuggestedLoading] = useState(false);
  // iter813 — Auto-reconciliation: match open advances against bank/
  // employee/partner payments to the same person. `recon` holds the
  // suggestion groups; auto-tier links on one click, suggested-tier is
  // confirm-per-row.
  const [recon, setRecon] = useState({ groups: [], auto_count: 0, suggested_count: 0 });
  const [reconLoading, setReconLoading] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);
  // iter815 — Overnight auto-settle banner from the nightly cron.
  const [overnight, setOvernight] = useState(null);
  // iter502 — Smart Presets library. Loaded once on mount, refreshed
  // when a preset is created/deleted/applied. Each preset can be
  // applied to ANY new statement to repeat the same bulk-convert.
  const [presets, setPresets] = useState([]);
  const [presetsOpen, setPresetsOpen] = useState(false);
  const [presetSaveName, setPresetSaveName] = useState('');
  const [applyingPreset, setApplyingPreset] = useState(null); // id of preset currently being applied

  // create-form state
  const [form, setForm] = useState({
    person_type: 'employee', person_id: '',
    paying_branch_id: '', paying_bank_account_id: '',
    payment_mode: 'bank',
    amount: '', date: localToday(),
    description: '', reference: '',
  });
  const [submitting, setSubmitting] = useState(false);

  // Inline "+ Record expense" modal — lets the user log a draw-down
  // against an advance without leaving this page. Parent stores the
  // target advance; when `expenseForm.open` is true we render a small
  // dialog wired to POST /pass-through-advances/{id}/record-expense.
  const [expenseAdvance, setExpenseAdvance] = useState(null);
  const [expenseForm, setExpenseForm] = useState({
    kind: 'expense', // expense | supplier_payment | salary_advance | partner_payment | loan_repayment
    category: '', vendor: '', amount: '', description: '', date: localToday(),
    supplier_id: '', supplier_name: '',
    employee_id: '', employee_name: '',
    partner_id: '', partner_name: '',
    loan_id: '', lender_name: '',
    payment_method: 'cash',
    payment_type: 'advance',
    salary_month: localToday().slice(0, 7),
    branch_id: '',  // iter824 — draw-down needs branch scope for branch-filtered P&L
  });
  const [expenseSubmitting, setExpenseSubmitting] = useState(false);

  const openExpenseFor = (adv) => {
    setExpenseAdvance(adv);
    setExpenseForm({
      kind: 'expense',
      category: '', vendor: adv.person_name || '', amount: '',
      description: '', date: localToday(),
      supplier_id: '', supplier_name: '',
      employee_id: adv.person_type === 'employee' ? (adv.person_id || '') : '',
      employee_name: adv.person_type === 'employee' ? (adv.person_name || '') : '',
      partner_id: adv.person_type === 'partner' ? (adv.person_id || '') : '',
      partner_name: adv.person_type === 'partner' ? (adv.person_name || '') : '',
      loan_id: '', lender_name: '',
      payment_method: 'cash',
      payment_type: 'advance',
      salary_month: localToday().slice(0, 7),
      branch_id: adv.paying_branch_id || '',  // iter824 — default to advance's branch
    });
  };

  const submitExpense = async () => {
    if (!expenseAdvance) return;
    const amt = Number(expenseForm.amount) || 0;
    if (amt <= 0) { toast.error('Amount must be greater than zero'); return; }

    // Per-kind required-field guard
    const k = expenseForm.kind;
    if (k === 'expense' && !expenseForm.category.trim()) {
      toast.error('Category is required for expense'); return;
    }
    if (k === 'supplier_payment' && !expenseForm.supplier_id) {
      toast.error('Pick a supplier'); return;
    }
    if (k === 'salary_advance' && !expenseForm.employee_id) {
      toast.error('Pick an employee'); return;
    }
    if (k === 'partner_payment' && !expenseForm.partner_id) {
      toast.error('Pick a partner'); return;
    }

    const payload = {
      kind: k,
      amount: amt,
      date: expenseForm.date,
      description: expenseForm.description.trim(),
      branch_id: expenseForm.branch_id || undefined,  // iter824 — pass explicit branch
      ...(k === 'expense' && {
        category: expenseForm.category.trim(),
        vendor: expenseForm.vendor.trim(),
        payment_method: expenseForm.payment_method,
      }),
      ...(k === 'supplier_payment' && {
        supplier_id: expenseForm.supplier_id,
        supplier_name: expenseForm.supplier_name,
      }),
      ...(k === 'salary_advance' && {
        employee_id: expenseForm.employee_id,
        employee_name: expenseForm.employee_name,
        payment_type: expenseForm.payment_type,
        ...(expenseForm.payment_type === 'salary' && { salary_month: expenseForm.salary_month }),
        payment_method: expenseForm.payment_method,
      }),
      ...(k === 'partner_payment' && {
        partner_id: expenseForm.partner_id,
        partner_name: expenseForm.partner_name,
      }),
      ...(k === 'loan_repayment' && {
        loan_id: expenseForm.loan_id,
        lender_name: expenseForm.lender_name,
      }),
    };

    setExpenseSubmitting(true);
    try {
      await api.post(`/pass-through-advances/${expenseAdvance.id}/record-downstream`, payload);
      toast.success(`${KIND_LABELS[k]} recorded — advance balance updated`);
      setExpenseAdvance(null);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to record draw-down');
    } finally {
      setExpenseSubmitting(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await api.get('/pass-through-advances');
      setData(r.data || { items: [], count: 0, open_total: 0 });
      try {
        const g = await api.get('/pass-through-advances/grouped');
        setGrouped(g.data || { groups: [], total_advance: 0, total_adjusted: 0, open_remaining: 0 });
      } catch { /* grouped is additive — never break the page */ }
    } finally { setLoading(false); }
  };

  // iter813 — Reconciliation suggestions + auto-link handlers.
  const fetchRecon = async () => {
    setReconLoading(true);
    try {
      const r = await api.get('/pass-through-advances/reconcile-suggestions', { params: { window_days: 400 } });
      setRecon(r.data || { groups: [], auto_count: 0, suggested_count: 0 });
    } catch { /* additive — never break the page */ } finally { setReconLoading(false); }
  };

  const fetchOvernight = async () => {
    try {
      const r = await api.get('/pass-through-advances/overnight-summary');
      setOvernight(r.data?.has_summary ? r.data : null);
    } catch { /* additive */ }
  };

  const dismissOvernight = async () => {
    setOvernight(null);
    try { await api.post('/pass-through-advances/overnight-summary/dismiss'); } catch { /* best-effort */ }
  };

  const handleAutoReconcile = async () => {
    setReconBusy(true);
    try {
      const r = await api.post('/pass-through-advances/auto-reconcile', null, { params: { window_days: 400 } });
      const n = r.data?.linked_count || 0;
      toast.success(n ? `Auto-linked ${n} exact match${n > 1 ? 'es' : ''} (${fmtSR(r.data?.total_amount || 0)})` : 'No 100%-confident matches to auto-link');
      await Promise.all([fetchData(), fetchRecon()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-reconcile failed');
    } finally { setReconBusy(false); }
  };

  const handleConfirmRecon = async (advanceId, collection, rowId) => {
    setReconBusy(true);
    try {
      const r = await api.post(`/pass-through-advances/${advanceId}/reconcile`, { collection, row_id: rowId });
      toast.success(r.data?.recon_status === 'fully_reconciled'
        ? 'Fully reconciled — advance settled'
        : `Partially reconciled — remaining ${fmtSR(r.data?.remaining || 0)}`);
      await Promise.all([fetchData(), fetchRecon()]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile failed');
    } finally { setReconBusy(false); }
  };
  // iter395 — Pull last-90-days outgoing bank_payments to employees /
  // partners that haven't been linked downstream. Each becomes a one-
  // click "Convert to Advance" suggestion. Mirrors the Banking Hub's
  // `+ Advance` action but in bulk-discovery form here.
  //
  // iter409 — Also pull bank-statement-detected employee/partner
  // transfers via /pass-through-advances/suggestions. Those rows live
  // on `bank_statements.transactions[]` (not `bank_payments`), so the
  // ledger query above misses them. The new endpoint walks the smart-
  // matcher's `link_type` stamp to surface them. We tag merged rows
  // with `_source` so the convert handler picks the right path.
  const fetchSuggested = async () => {
    setSuggestedLoading(true);
    try {
      // Fetch employee + partner outgoing rows in parallel + the new
      // bank-statement suggestions endpoint.
      const [emp, par, stmt] = await Promise.all([
        api.get('/banking/ledger', { params: { period: 'quarter', payee_type: 'employee', direction: 'outgoing', limit: 100 } }).catch(() => ({ data: { rows: [] } })),
        api.get('/banking/ledger', { params: { period: 'quarter', payee_type: 'partner', direction: 'outgoing', limit: 100 } }).catch(() => ({ data: { rows: [] } })),
        api.get('/pass-through-advances/suggestions').catch(() => ({ data: { items: [] } })),
      ]);
      const ledgerRows = [...(emp.data?.rows || []), ...(par.data?.rows || [])]
        .filter((r) => r.raw_collection === 'bank_payments'
          && r.payee_id
          && (r.linked_downstream_ids?.length || 0) === 0)
        .map((r) => ({ ...r, _source: 'bank_payment' }));
      // Normalise statement suggestions into the same row shape used
      // throughout the existing UI so we can render them with the same
      // template.
      const stmtRows = (stmt.data?.items || []).map((s) => ({
        id: s.ref,                      // sentinel — we use _source to dispatch
        _source: 'statement_txn',
        _suggestion_ref: s.ref,
        ts: s.date,
        date: s.date,
        amount: s.amount,
        kind: s.person_type,
        party_name: s.person_name,
        payee_id: s.person_id,
        description: s.description,
        reference: s.reference,
        paying_branch_id: s.paying_branch_id,
        bank_account_id: s.paying_bank_account_id,
        raw_party_name: s.raw_party_name,
        auto_link_confidence: s.auto_link_confidence,
        // iter533 — same-month offset fields from the backend
        _already_booked: s.already_booked,
        _remaining: s.remaining,
        _offset_count: s.offset_count,
        _offset_sample: s.offset_sample,
        _auto_consumed: s.auto_consumed,
        _year_month: s.year_month,
      }));
      const fresh = [...ledgerRows, ...stmtRows]
        .sort((a, b) => (b.ts || '').localeCompare(a.ts || ''))
        .slice(0, 60);  // iter560 — was 12; broad IBAN/name scan now surfaces many more candidates
      setSuggested(fresh);
    } finally { setSuggestedLoading(false); }
  };

  // Convert a suggested row to a Pass-Through Advance. Routes by
  // `_source` so bank_statement suggestions hit the dedicated endpoint
  // (which also stamps `funding_linked_at` on the txn so it can't be
  // re-suggested). Falls back to the iter395 path for ledger rows.
  const convertSuggestionToAdvance = async (row) => {
    try {
      if (row._source === 'statement_txn') {
        await api.post('/pass-through-advances/from-suggestion', {
          ref: row._suggestion_ref,
          description: row.description || `Advance from bank transfer to ${row.party_name}`,
        });
      } else {
        await api.post('/pass-through-advances', {
          person_type: row.kind,
          person_id: row.payee_id,
          paying_branch_id: row.paying_branch_id,
          paying_bank_account_id: row.bank_account_id,
          payment_mode: row.bank_account_id ? 'bank' : 'cash',
          amount: row.amount,
          date: (row.ts || '').slice(0, 10),
          description: row.description || row.party_name || 'Advance from bank statement',
          reference: row.reference,
          bank_payment_id: row.id,
        });
      }
      toast.success(`Advance created for ${row.party_name}`);
      // Drop from the suggestions list and refresh advances.
      setSuggested((prev) => prev.filter((r) => r.id !== row.id));
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create advance');
    }
  };

  // iter500 — Direct convert a statement_txn suggestion to ANY of the 5
  // downstream kinds (expense / supplier_payment / salary_advance /
  // partner_payment) without first creating a Pass-Through Advance.
  // Routes to the dedicated /from-suggestion-direct endpoint, which
  // also stamps `funding_linked_at` on the txn so it disappears from
  // every suggestion inbox. For ledger-row suggestions (older path)
  // we fall back to going via Advance because they lack a statement
  // txn_ref.
  const [directConvertOpen, setDirectConvertOpen] = useState(false);
  const [directConvertRow, setDirectConvertRow] = useState(null);
  const [directConvertKind, setDirectConvertKind] = useState('expense');
  const [directConvertForm, setDirectConvertForm] = useState({
    category: '', vendor: '', payment_method: 'bank',
    supplier_id: '', supplier_name: '',
    employee_id: '', employee_name: '',
    partner_id: '', partner_name: '',
    description: '', date: localToday(),
  });
  const [directConvertSubmitting, setDirectConvertSubmitting] = useState(false);

  const openDirectConvert = (row, kind) => {
    if (row._source !== 'statement_txn') {
      toast.info('This suggestion can only be converted via Advance — use the Advance button.');
      return;
    }
    setDirectConvertRow(row);
    setDirectConvertKind(kind);
    // Pre-fill from the row when possible. Employee/partner kinds get
    // the resolved person pre-selected so the user just confirms.
    setDirectConvertForm({
      category: '',
      vendor: row.party_name || row.raw_party_name || '',
      payment_method: 'bank',
      supplier_id: '',
      supplier_name: '',
      employee_id: row.kind === 'employee' ? (row.payee_id || '') : '',
      employee_name: row.kind === 'employee' ? (row.party_name || '') : '',
      partner_id: row.kind === 'partner' ? (row.payee_id || '') : '',
      partner_name: row.kind === 'partner' ? (row.party_name || '') : '',
      description: row.description || '',
      date: (row.date || row.ts || '').slice(0, 10) || localToday(),
    });
    setDirectConvertOpen(true);
  };

  const submitDirectConvert = async () => {
    if (!directConvertRow?._suggestion_ref) {
      toast.error('Missing suggestion reference'); return;
    }
    const k = directConvertKind;
    if (k === 'expense' && !directConvertForm.category.trim()) {
      toast.error('Category is required for expense'); return;
    }
    if (k === 'supplier_payment' && !directConvertForm.supplier_id) {
      toast.error('Pick a supplier'); return;
    }
    if (k === 'salary_advance' && !directConvertForm.employee_id) {
      toast.error('Pick an employee'); return;
    }
    if (k === 'partner_payment' && !directConvertForm.partner_id) {
      toast.error('Pick a partner'); return;
    }
    setDirectConvertSubmitting(true);
    try {
      const payload = {
        ref: directConvertRow._suggestion_ref,
        kind: k,
        description: directConvertForm.description.trim(),
        date: directConvertForm.date,
        ...(k === 'expense' && {
          category: directConvertForm.category.trim(),
          vendor: directConvertForm.vendor.trim(),
          payment_method: directConvertForm.payment_method,
        }),
        ...(k === 'supplier_payment' && {
          supplier_id: directConvertForm.supplier_id,
          supplier_name: directConvertForm.supplier_name,
        }),
        ...(k === 'salary_advance' && {
          employee_id: directConvertForm.employee_id,
          employee_name: directConvertForm.employee_name,
        }),
        ...(k === 'partner_payment' && {
          partner_id: directConvertForm.partner_id,
          partner_name: directConvertForm.partner_name,
        }),
      };
      await api.post('/pass-through-advances/from-suggestion-direct', payload);
      toast.success(`${KIND_LABELS[k] || k} created from bank row`);
      setDirectConvertOpen(false);
      setSuggested((prev) => prev.filter((r) => r.id !== directConvertRow.id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to convert');
    } finally {
      setDirectConvertSubmitting(false);
    }
  };

  const dismissSuggestion = (row) => {
    setSuggested((prev) => prev.filter((r) => r.id !== row.id));
  };

  // iter502 — Smart Presets fetch/save/apply/delete.
  const fetchPresets = async () => {
    try {
      const r = await api.get('/bulk-convert-presets');
      setPresets(r.data?.presets || []);
    } catch { /* silent */ }
  };

  const savePresetFromBulk = async () => {
    const name = (presetSaveName || '').trim();
    if (!name) { toast.error('Give the preset a name'); return; }
    if (!bulkAnchor) return;
    try {
      const payload = {
        name,
        match_by: bulkMatchBy,
        kind: bulkKind,
        include_anchor: bulkForm.include_anchor,
        defaults: {
          ...(bulkKind === 'expense' && { category: bulkForm.category.trim(), vendor: bulkForm.vendor.trim(), payment_method: bulkForm.payment_method }),
          ...(bulkKind === 'supplier_payment' && { supplier_id: bulkForm.supplier_id, supplier_name: bulkForm.supplier_name }),
          ...(bulkKind === 'salary_advance' && { employee_id: bulkForm.employee_id, employee_name: bulkForm.employee_name }),
          ...(bulkKind === 'partner_payment' && { partner_id: bulkForm.partner_id, partner_name: bulkForm.partner_name }),
        },
        ...(bulkMatchBy === 'signature' && { signature: bulkPreview.anchor_signature }),
        ...(bulkMatchBy === 'person' && {
          person_link: {
            link_type: bulkAnchor.kind,
            link_id: bulkAnchor.payee_id,
            link_name: bulkAnchor.party_name,
          },
        }),
      };
      await api.post('/bulk-convert-presets', payload);
      toast.success(`Preset "${name}" saved`);
      setPresetSaveName('');
      fetchPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save preset');
    }
  };

  const applyPreset = async (preset, opts = {}) => {
    setApplyingPreset(preset.id);
    try {
      // First preview to know the count
      const prev = await api.post(`/bulk-convert-presets/${preset.id}/apply`, { preview_only: true });
      const matched = prev.data?.matched || 0;
      if (matched === 0) {
        toast.info(`No un-linked rows match "${preset.name}" right now.`);
        return;
      }
      const confirmLarge = matched > 50;
      if (confirmLarge && !opts.skipConfirm) {
        if (!window.confirm(`Preset "${preset.name}" will convert ${matched} rows (${cur()} ${(prev.data.total_amount || 0).toLocaleString()}). Continue?`)) return;
      } else if (!opts.skipConfirm) {
        if (!window.confirm(`Apply preset "${preset.name}" — convert ${matched} row${matched === 1 ? '' : 's'}?`)) return;
      }
      const r = await api.post(`/bulk-convert-presets/${preset.id}/apply`, {
        confirm_large_batch: confirmLarge,
      });
      const c = r.data?.converted || 0;
      const f = (r.data?.failed || []).length;
      toast.success(`Preset applied · ${c} converted${f ? ` · ${f} failed` : ''}`);
      fetchPresets();
      fetchSuggested();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to apply preset');
    } finally {
      setApplyingPreset(null);
    }
  };

  const deletePreset = async (preset) => {
    if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
    try {
      await api.delete(`/bulk-convert-presets/${preset.id}`);
      toast.success('Preset deleted');
      fetchPresets();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed');
    }
  };

  // iter501 — Bulk Convert Similar Rows.
  // Strategy: user clicks "Bulk" on a suggestion row → we GET
  // /find-similar to preview N matches (by description signature OR
  // by same resolved person), the user picks a target kind + provides
  // any required defaults, then we POST /bulk-convert-similar which
  // creates one row per match + stamps every txn so they vanish from
  // every inbox.
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkAnchor, setBulkAnchor] = useState(null);
  const [bulkMatchBy, setBulkMatchBy] = useState('signature');
  const [bulkKind, setBulkKind] = useState('expense');
  const [bulkPreview, setBulkPreview] = useState({ count: 0, total_amount: 0, samples: [], loading: false, anchor_signature: '' });
  const [bulkForm, setBulkForm] = useState({
    category: '', vendor: '', payment_method: 'bank',
    supplier_id: '', supplier_name: '',
    employee_id: '', employee_name: '',
    partner_id: '', partner_name: '',
    include_anchor: true,
  });
  const [bulkSubmitting, setBulkSubmitting] = useState(false);

  const fetchBulkPreview = async (ref, match_by) => {
    setBulkPreview((p) => ({ ...p, loading: true }));
    try {
      const r = await api.get('/pass-through-advances/find-similar', { params: { ref, match_by } });
      setBulkPreview({
        count: r.data?.count || 0,
        total_amount: r.data?.total_amount || 0,
        samples: r.data?.samples || [],
        anchor_signature: r.data?.anchor_signature || '',
        loading: false,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to find similar rows');
      setBulkPreview({ count: 0, total_amount: 0, samples: [], anchor_signature: '', loading: false });
    }
  };

  const openBulkConvert = (row) => {
    if (row._source !== 'statement_txn') {
      toast.info('Bulk convert only works for bank-statement suggestions.');
      return;
    }
    setBulkAnchor(row);
    setBulkKind('expense');
    const hasPerson = !!(row.payee_id && (row.kind === 'employee' || row.kind === 'partner'));
    const initialMatchBy = hasPerson ? 'person' : 'signature';
    setBulkMatchBy(initialMatchBy);
    setBulkForm({
      category: '', vendor: row.party_name || row.raw_party_name || '', payment_method: 'bank',
      supplier_id: '', supplier_name: '',
      employee_id: row.kind === 'employee' ? (row.payee_id || '') : '',
      employee_name: row.kind === 'employee' ? (row.party_name || '') : '',
      partner_id: row.kind === 'partner' ? (row.payee_id || '') : '',
      partner_name: row.kind === 'partner' ? (row.party_name || '') : '',
      include_anchor: true,
    });
    setBulkOpen(true);
    fetchBulkPreview(row._suggestion_ref, initialMatchBy);
  };

  const onBulkMatchByChange = (v) => {
    setBulkMatchBy(v);
    if (bulkAnchor?._suggestion_ref) fetchBulkPreview(bulkAnchor._suggestion_ref, v);
  };

  const submitBulkConvert = async () => {
    if (!bulkAnchor?._suggestion_ref) { toast.error('Missing anchor'); return; }
    const k = bulkKind;
    if (k === 'expense' && !bulkForm.category.trim()) { toast.error('Category is required'); return; }
    if (k === 'supplier_payment' && !bulkForm.supplier_id) { toast.error('Pick a supplier'); return; }
    if (k === 'salary_advance' && !bulkForm.employee_id && bulkMatchBy !== 'person') {
      toast.error('Pick an employee (or use match_by=person)'); return;
    }
    if (k === 'partner_payment' && !bulkForm.partner_id && bulkMatchBy !== 'person') {
      toast.error('Pick a partner (or use match_by=person)'); return;
    }
    // iter501 — Safety prompt for batches > 50 rows. Backend rejects
    // with 409 unless `confirm_large_batch: true` is in the payload.
    let confirmLarge = false;
    if (bulkPreview.count > 50) {
      const yes = window.confirm(
        `You're about to convert ${bulkPreview.count} rows in one batch. ` +
        `This is irreversible (each row creates a permanent record). Continue?`
      );
      if (!yes) return;
      confirmLarge = true;
    }
    setBulkSubmitting(true);
    try {
      const payload = {
        ref: bulkAnchor._suggestion_ref,
        match_by: bulkMatchBy,
        kind: k,
        include_anchor: bulkForm.include_anchor,
        confirm_large_batch: confirmLarge,
        ...(k === 'expense' && {
          category: bulkForm.category.trim(),
          vendor: bulkForm.vendor.trim(),
          payment_method: bulkForm.payment_method,
        }),
        ...(k === 'supplier_payment' && {
          supplier_id: bulkForm.supplier_id,
          supplier_name: bulkForm.supplier_name,
        }),
        ...(k === 'salary_advance' && {
          employee_id: bulkForm.employee_id || undefined,
          employee_name: bulkForm.employee_name || undefined,
        }),
        ...(k === 'partner_payment' && {
          partner_id: bulkForm.partner_id || undefined,
          partner_name: bulkForm.partner_name || undefined,
        }),
        ...(k === 'advance' && {
          person_type: bulkAnchor.kind,
          person_id: bulkAnchor.payee_id,
        }),
      };
      const r = await api.post('/pass-through-advances/bulk-convert-similar', payload);
      const c = r.data?.converted || 0;
      const f = (r.data?.failed || []).length;
      toast.success(`Converted ${c} row${c === 1 ? '' : 's'}${f ? ` · ${f} failed` : ''}`);
      setBulkOpen(false);
      // Remove the anchor + any other suggestions known to be linked
      // by this batch (we don't know exact refs here for matched rows,
      // so just refetch the suggestions to refresh the inbox).
      setSuggested((prev) => prev.filter((rr) => rr.id !== bulkAnchor.id));
      fetchSuggested();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk convert failed');
    } finally {
      setBulkSubmitting(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchSuggested();
    fetchRecon();
    fetchOvernight();
    fetchPresets();
    Promise.all([
      api.get('/employees').catch(() => ({ data: [] })),
      api.get('/partners').catch(() => ({ data: [] })),
      api.get('/suppliers').catch(() => ({ data: [] })),
      api.get('/customers').catch(() => ({ data: [] })),
      api.get('/bank-accounts?active_only=true').catch(() => ({ data: [] })),
      api.get('/branches').catch(() => ({ data: [] })),
    ]).then(([e, p, s, c, ba, b]) => {
      setPeople({
        employees: (Array.isArray(e.data) ? e.data : (e.data?.employees || []))
          .filter(x => x?.id).map(x => ({ id: x.id, label: x.name || x.full_name || '—' })),
        partners: (Array.isArray(p.data) ? p.data : (p.data?.partners || []))
          .filter(x => x?.id).map(x => ({ id: x.id, label: x.name || '—' })),
        suppliers: (Array.isArray(s.data) ? s.data : (s.data?.suppliers || s.data?.data || []))
          .filter(x => x?.id).map(x => ({ id: x.id, label: x.name || '—' })),
        customers: (Array.isArray(c.data) ? c.data : (c.data?.customers || c.data?.data || []))
          .filter(x => x?.id).map(x => ({ id: x.id, label: x.name || '—' })),
      });
      setBankAccounts(Array.isArray(ba.data) ? ba.data : (ba.data?.accounts || []));
      setBranches(Array.isArray(b.data) ? b.data : (b.data?.branches || []));
    });
  }, []);

  const filtered = useMemo(() => {
    if (filter === 'all') return data.items;
    return data.items.filter(it => it.status === filter);
  }, [data.items, filter]);

  // iter824 — Group multiple advances of the same person into one collapsible
  // card. When a person (e.g. "Yasir") has 3 advances open, we render one
  // "Yasir" summary card with combined amount/consumed/remaining, and expand
  // to reveal the 3 individual advances underneath. Falls back to a flat
  // one-per-card layout when nobody has more than a single advance.
  const groupedFiltered = useMemo(() => {
    const byPerson = new Map();
    for (const adv of filtered) {
      const key = `${adv.person_type || ''}::${adv.person_id || adv.person_name || 'unknown'}`;
      if (!byPerson.has(key)) {
        byPerson.set(key, {
          key,
          person_id: adv.person_id,
          person_name: adv.person_name || '—',
          person_type: adv.person_type,
          advances: [],
        });
      }
      byPerson.get(key).advances.push(adv);
    }
    const groups = [...byPerson.values()].map(g => {
      const amount = g.advances.reduce((s, a) => s + (a.amount || 0), 0);
      const consumed = g.advances.reduce((s, a) => s + (a.consumed || 0), 0);
      const remaining = g.advances.reduce((s, a) => s + (a.remaining || 0), 0);
      // Group status: over > open > fully_consumed (worst-first for badge).
      const hasOver = g.advances.some(a => a.status === 'over_consumed');
      const hasOpen = g.advances.some(a => a.status === 'open');
      const status = hasOver ? 'over_consumed' : hasOpen ? 'open' : 'fully_consumed';
      // Newest advance first inside the group so recent activity is on top.
      g.advances.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
      return { ...g, amount, consumed, remaining, status, count: g.advances.length };
    });
    // Sort groups by open remaining desc so the biggest outstanding rises to the top.
    groups.sort((a, b) => b.remaining - a.remaining);
    return groups;
  }, [filtered]);

  // Per-group expand state — only render sub-cards when the user opts in.
  const [expandedGroups, setExpandedGroups] = useState({});
  const toggleGroup = (key) => setExpandedGroups(s => ({ ...s, [key]: !s[key] }));

  // iter825 — Suggestions grouping. The "Suggested from bank statements"
  // list often contains 5-10 rows for the same person (Yasir was paid 3
  // times, Faisal 8 times, etc.). Rendering each row separately turns
  // this section into a wall — instead we group them by party name so
  // "Yasir · 3 payments · SR 20,500" shows as a single collapsible card
  // that opens to the individual rows on click.
  const groupedSuggestions = useMemo(() => {
    const byParty = new Map();
    for (const row of suggested) {
      const key = (row.party_name || row.raw_party_name || row.description || '—').trim().toLowerCase();
      if (!byParty.has(key)) {
        byParty.set(key, {
          key,
          party_name: row.party_name || row.raw_party_name || '—',
          rows: [],
        });
      }
      byParty.get(key).rows.push(row);
    }
    const groups = [...byParty.values()].map(g => {
      const total = g.rows.reduce((s, r) => s + (r.amount || 0), 0);
      // Sort newest first inside a group.
      g.rows.sort((a, b) => String(b.ts || '').localeCompare(String(a.ts || '')));
      return { ...g, total, count: g.rows.length };
    });
    // Biggest exposure first.
    groups.sort((a, b) => b.total - a.total);
    return groups;
  }, [suggested]);

  // Derive the right people-list based on the selected type. Supplier
  // and customer support was added in iter354 — gives finance teams a
  // way to track prepayments and overpayment credits via the same
  // pass-through ledger they already use for staff / partner advances.
  const personOptions = (
    form.person_type === 'employee' ? people.employees :
    form.person_type === 'partner' ? people.partners :
    form.person_type === 'supplier' ? people.suppliers :
    form.person_type === 'customer' ? people.customers :
    []
  );

  const submit = async () => {
    if (!form.person_id) { toast.error(`Pick an ${form.person_type}`); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Enter amount'); return; }
    if (form.payment_mode === 'bank' && !form.paying_bank_account_id) { toast.error('Pick bank account'); return; }
    setSubmitting(true);
    try {
      await api.post('/pass-through-advances', {
        ...form,
        amount: parseFloat(form.amount),
      });
      toast.success(`Advance recorded · ${fmtSR(form.amount)}`);
      setCreateOpen(false);
      setForm(f => ({ ...f, person_id: '', amount: '', description: '', reference: '' }));
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record');
    } finally { setSubmitting(false); }
  };

  const openDetail = async (advance) => {
    try {
      const r = await api.get(`/pass-through-advances/${advance.id}`);
      setDetailFor(r.data);
    } catch {
      toast.error('Failed to load detail');
    }
  };

  const onDelete = async (advance) => {
    if (!window.confirm(`Delete advance to ${advance.person_name}? Linked expenses will be kept.`)) return;
    try {
      await api.delete(`/pass-through-advances/${advance.id}`);
      toast.success('Deleted');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  // iter825 — Reusable row renderer for the "Suggested from bank statements"
  // section. Used both for single-payment persons (unchanged from the old
  // flat layout) and for individual payments inside an expanded person
  // group.
  const renderSuggestionRow = (row, opts = {}) => (
    <div
      key={row.id}
      className={`px-3 py-2.5 flex items-center gap-3 ${opts.indent ? 'pl-8 bg-transparent' : `rounded-lg border ${row._auto_consumed ? 'border-amber-300 bg-amber-50/60 dark:bg-amber-950/20' : 'border-violet-200 bg-white'}`}`}
      data-testid={`advance-suggestion-${row.id}`}
    >
      {!opts.indent && <Banknote size={16} className="text-violet-600 shrink-0" />}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold truncate">
          {row.party_name || row.raw_party_name || (row.description ? `(from: ${row.description.slice(0, 50)})` : '—')}
        </div>
        {row._source === 'statement_txn' && row.raw_party_name
          && row.raw_party_name.toUpperCase() !== (row.party_name || '').toUpperCase() && (
          <div className="text-[10px] text-muted-foreground/70 truncate" title={row.raw_party_name}>
            from bank: {row.raw_party_name}
          </div>
        )}
        <div className="text-[10px] text-muted-foreground inline-flex items-center gap-1.5 mt-0.5 flex-wrap">
          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{row.kind}</Badge>
          <span>·</span>
          <span>{(row.ts || '').slice(0, 10)}</span>
          {row._source === 'statement_txn' && (
            <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200" title={`Auto-resolved by ${row.auto_link_confidence || 'matcher'}`}>
              auto-matched
            </Badge>
          )}
          {row.bank_account_name && (<><span>·</span><span className="truncate max-w-[100px]">{row.bank_account_name}</span></>)}
        </div>
        {row._source === 'statement_txn' && row._already_booked > 0 && (
          <div className="mt-1 text-[10px] font-medium" data-testid={`offset-badge-${row.id}`}>
            {row._auto_consumed ? (
              <span className="text-amber-700 dark:text-amber-400">
                ✓ Already paid {cur()} {row._already_booked.toFixed(2)} in {row._year_month || 'this month'} ({row._offset_count} expense{row._offset_count !== 1 ? 's' : ''}) — likely duplicate
              </span>
            ) : (
              <span className="text-orange-600 dark:text-orange-400">
                ⚠ Partial: {cur()} {row._already_booked.toFixed(2)} already paid this month · remaining {cur()} {row._remaining.toFixed(2)}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="font-bold tabular-nums text-rose-700 text-sm">{fmtSR(row.amount)}</div>
      </div>
      <div className="flex items-center gap-1 shrink-0 flex-wrap justify-end">
        <Button size="sm" className="h-7 text-[10px] bg-violet-600 hover:bg-violet-700"
          onClick={() => convertSuggestionToAdvance(row)}
          data-testid={`advance-suggestion-accept-${row.id}`}
          title="Create a Pass-Through Advance from this bank payment">
          <Plus size={10} className="mr-1" /> Advance
        </Button>
        {row._source === 'statement_txn' && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-[10px]"
              onClick={() => openDirectConvert(row, 'expense')}
              data-testid={`advance-suggestion-as-expense-${row.id}`} title="Record directly as an Expense">Expense</Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px]"
              onClick={() => openDirectConvert(row, 'salary_advance')}
              data-testid={`advance-suggestion-as-salary-${row.id}`} title="Record directly as a Salary / Employee Advance">Salary</Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px]"
              onClick={() => openDirectConvert(row, 'supplier_payment')}
              data-testid={`advance-suggestion-as-supplier-${row.id}`} title="Record directly as a Supplier Payment">Supplier</Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px]"
              onClick={() => openDirectConvert(row, 'partner_payment')}
              data-testid={`advance-suggestion-as-partner-${row.id}`} title="Record directly as a Partner Payment / Draw">Partner</Button>
            <Button size="sm" variant="outline" className="h-7 text-[10px] border-amber-300 text-amber-700 hover:bg-amber-50"
              onClick={() => openBulkConvert(row)}
              data-testid={`advance-suggestion-bulk-${row.id}`} title="Find similar rows and convert them all in one go">
              <Sparkles size={10} className="mr-1" /> Bulk
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0"
          onClick={() => dismissSuggestion(row)}
          data-testid={`advance-suggestion-dismiss-${row.id}`} title="Hide this suggestion (not an advance)">
          <X size={11} />
        </Button>
      </div>
    </div>
  );

  return (
    <DashboardLayout module="finance">
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="advances-title">
              <HandCoins size={22} /> Pass-Through Advances
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Track money handed to employees, partners, suppliers (prepayments), or customers (overpayment credits) — and reconcile each downstream draw-down (expense, salary advance, supplier payment, customer payment, partner draw) back to its funding source.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)} className="rounded-xl" data-testid="advance-new-btn">
            <Plus size={14} className="mr-1.5" /> New Advance
          </Button>
        </div>

        <PageInfoCallout
          storageKey="pass-through-advances"
          title="What are Pass-Through Advances?"
          tone="emerald"
        >
          <p>
            <strong>Money that&apos;s in your account but isn&apos;t really yours.</strong> Examples:
          </p>
          <ul className="list-disc ml-5 mt-1 space-y-0.5">
            <li>A partner sends 10,000 SAR for you to buy stock on their behalf</li>
            <li>A customer overpays and you owe them a refund</li>
            <li>You handed an employee 500 SAR petty cash to spend on fuel</li>
          </ul>
          <p className="mt-2">
            Don&apos;t book it as revenue or expense — that would distort your P&amp;L. Record it as an
            <strong> advance</strong>. When the money goes out again (stock purchase, refund, transfer), link
            the draw-down here and the advance auto-clears — <em>net-zero impact on your books</em>.
          </p>
        </PageInfoCallout>
        <BankingFlowRecap active="/pass-through-advances" />

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Open Balance</div>
            <div className="text-2xl font-bold tabular-nums mt-1 text-amber-700 dark:text-amber-300" data-testid="advances-open-total">
              {fmtSR(data.open_total)}
            </div>
            <div className="text-[10px] text-muted-foreground">
              Sum of unaccounted-for advances · click "open" filter below
            </div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Total Advances</div>
            <div className="text-2xl font-bold tabular-nums mt-1">{data.count}</div>
            <div className="text-[10px] text-muted-foreground">All time</div>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <div className="text-xs uppercase tracking-wider font-bold text-muted-foreground">Tip</div>
            <div className="text-xs mt-1">
              Every <span className="font-semibold">expense</span> or <span className="font-semibold">supplier-payment</span> with a <code className="text-[10px] bg-stone-100 px-1 rounded">funded_by_advance_id</code> field is automatically deducted from the advance balance.
            </div>
          </CardContent></Card>
        </div>

        <GettingStartedChecklist
          storageKey="pass-through-v1"
          title="Start tracking advances"
          steps={[
            { key: 'create', label: 'Record your first advance to an employee or partner', onClick: () => setCreateOpen(true), cta: 'New advance' },
            { key: 'record-expense', label: 'Log an expense drawn from that advance', href: '#', cta: 'I will do this' },
            { key: 'link-existing', label: 'Attach an existing expense/supplier-payment to an advance', href: '#', cta: 'I will do this' },
            { key: 'reconcile', label: 'Open an advance\u2019s detail view to see its audit trail', href: '#', cta: 'I will do this' },
          ]}
        />

        {/* iter815 — Overnight auto-settle banner. Surfaces what the nightly
            cron linked while the user was away; dismissible (marks runs seen). */}
        {overnight && (
          <div className="rounded-xl border border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50/50 px-4 py-3 flex items-center gap-3" data-testid="overnight-recon-banner">
            <div className="h-9 w-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle2 size={18} className="text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-emerald-900" data-testid="overnight-recon-count">
                {overnight.linked_count} advance{overnight.linked_count > 1 ? 's' : ''} auto-settled overnight
              </div>
              <div className="text-[11px] text-emerald-700 truncate">
                {fmtSR(overnight.total_amount)} matched to bank/employee/partner payments · {overnight.linked.slice(0, 4).map(l => l.person_name).join(', ')}{overnight.linked.length > 4 ? ` +${overnight.linked.length - 4} more` : ''}
              </div>
            </div>
            <Button size="sm" variant="ghost" className="h-7 text-[11px] text-emerald-700 hover:text-emerald-900" onClick={dismissOvernight} data-testid="overnight-recon-dismiss">
              Dismiss
            </Button>
          </div>
        )}

        {/* iter813 — Auto-Reconciliation panel. Matches OPEN advances against
            bank/employee/partner payments to the same person. Exact same-branch
            matches auto-link on one click; everything else is confirm-per-row. */}
        {(recon.auto_count > 0 || recon.suggested_count > 0) && (
          <Card className="border-blue-200 bg-gradient-to-r from-blue-50/70 via-sky-50/30 to-transparent" data-testid="recon-suggestions-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Link2 size={15} className="text-blue-600" />
                <h3 className="text-sm font-semibold text-blue-900">Suggested Reconciliations</h3>
                {recon.auto_count > 0 && <Badge className="bg-blue-600 text-white text-[10px]" data-testid="recon-auto-count">{recon.auto_count} exact</Badge>}
                {recon.suggested_count > 0 && <Badge variant="outline" className="text-[10px]" data-testid="recon-suggested-count">{recon.suggested_count} to review</Badge>}
                <div className="ml-auto flex items-center gap-2">
                  <Button size="sm" variant="ghost" className="h-7 text-[10px]" onClick={fetchRecon} disabled={reconLoading} data-testid="recon-refresh">Refresh</Button>
                  {recon.auto_count > 0 && (
                    <Button size="sm" className="h-7 text-[11px] bg-blue-600 hover:bg-blue-700" onClick={handleAutoReconcile} disabled={reconBusy} data-testid="recon-auto-link-all">
                      <CheckCircle2 size={13} className="mr-1" />Auto-link {recon.auto_count} exact
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
                Bank / employee / partner payments to the same person that can settle an open advance (same branch, matching amount, within 400 days). <strong>Exact</strong> matches (same branch + exact amount) link automatically; review the rest below.
              </p>
              <div className="space-y-2">
                {recon.groups.map((g) => (
                  <div key={g.advance_id} className="rounded-lg border border-blue-200 bg-white px-3 py-2.5" data-testid={`recon-group-${g.advance_id}`}>
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <span className="text-sm font-semibold">{g.person_name}</span>
                      <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{g.person_type}</Badge>
                      <span className="text-[11px] text-muted-foreground">Advance {fmtSR(g.advance_amount)} · Remaining <span className="font-semibold text-amber-600">{fmtSR(g.remaining)}</span></span>
                    </div>
                    <div className="space-y-1.5">
                      {[...g.auto, ...g.suggested].map((c) => (
                        <div key={`${c.collection}-${c.row_id}`} className="flex items-center gap-2 flex-wrap text-[11px] border-t border-border/40 pt-1.5" data-testid={`recon-cand-${c.row_id}`}>
                          <Banknote size={13} className={c.same_branch ? 'text-emerald-600' : 'text-muted-foreground'} />
                          <span className="font-mono font-semibold">{fmtSR(c.amount)}</span>
                          <span className="text-muted-foreground">{c.date}</span>
                          <Badge variant="outline" className="text-[9px]">{c.collection.replace(/_/g, ' ')}</Badge>
                          {c.auto_linkable
                            ? <Badge className="bg-blue-600 text-white text-[9px]">Exact</Badge>
                            : <Badge variant="secondary" className="text-[9px]">{c.reason}</Badge>}
                          {!c.same_branch && <Badge variant="outline" className="text-[9px] text-amber-600">diff branch</Badge>}
                          <span className="text-muted-foreground truncate max-w-[220px]">{c.description}</span>
                          <Button size="sm" variant={c.auto_linkable ? 'default' : 'outline'} className="ml-auto h-6 text-[10px] px-2" disabled={reconBusy}
                            onClick={() => handleConfirmRecon(g.advance_id, c.collection, c.row_id)} data-testid={`recon-link-${c.row_id}`}>
                            Link · settles {fmtSR(c.settles)}
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* iter501 — Bank Match Auto-suggest banner. Scans the tenant's
            last 90-days of outgoing bank_payments to employees/partners
            that aren't yet linked anywhere — letting the user convert
            them to a Pass-Through Advance in one click rather than
            re-typing the data. Hidden when nothing to suggest. */}
        {/* iter502 — Smart Presets Library. Saved bulk-convert configs
            re-runnable in one click against every un-linked bank txn
            currently in the tenant. Hidden when empty unless user is
            actively creating their first one. */}
        {presets.length > 0 && (
          <Card className="border-emerald-200 bg-gradient-to-r from-emerald-50/60 via-teal-50/30 to-transparent" data-testid="presets-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-emerald-600" />
                <h3 className="text-sm font-semibold text-emerald-900">Smart Presets ({presets.length})</h3>
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-[10px]" onClick={fetchPresets} data-testid="presets-refresh">
                  Refresh
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
                Saved bulk-convert configurations. Click <strong>Apply</strong> to instantly classify every un-linked bank row matching this preset across all your statements.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {presets.map((p) => (
                  <div key={p.id} className="rounded-lg border border-emerald-200 bg-white px-3 py-2.5 flex items-center gap-3" data-testid={`preset-row-${p.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold truncate" data-testid={`preset-name-${p.id}`}>{p.name}</div>
                      <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1 flex-wrap">
                        <Badge variant="outline" className="text-[9px] uppercase tracking-wider">{p.kind}</Badge>
                        <span>·</span>
                        <span>{p.match_by === 'signature' ? `sig: ${p.signature}` : `person: ${p.person_link?.link_name || p.person_link?.link_id}`}</span>
                        {p.apply_count > 0 && (<><span>·</span><span>applied {p.apply_count}×</span></>)}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      className="h-7 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                      onClick={() => applyPreset(p)}
                      disabled={applyingPreset === p.id}
                      data-testid={`preset-apply-${p.id}`}
                    >
                      {applyingPreset === p.id ? '…' : 'Apply'}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7 w-7 p-0 text-rose-600"
                      onClick={() => deletePreset(p)}
                      data-testid={`preset-delete-${p.id}`}
                      title="Delete preset"
                    >
                      <Trash2 size={11} />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
        {(suggested.length > 0 || suggestedLoading) && (
          <Card className="border-violet-200 bg-gradient-to-r from-violet-50/60 via-fuchsia-50/30 to-transparent" data-testid="advances-suggestions-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <Sparkles size={14} className="text-violet-600" />
                <h3 className="text-sm font-semibold text-violet-900">Suggested from bank statements</h3>
                <Badge variant="outline" className="text-[10px] bg-violet-50 text-violet-700 border-violet-200">{suggested.length}</Badge>
                <Button size="sm" variant="ghost" className="ml-auto h-7 text-[10px]" onClick={fetchSuggested} disabled={suggestedLoading} data-testid="advances-suggestions-refresh">
                  Refresh
                </Button>
              </div>
              <p className="text-[11px] text-muted-foreground mb-3 leading-relaxed">
                Bank transfers detected for employees / partners that aren&apos;t linked yet. Pulled from both un-converted bank-payment rows AND smart-matched bank-statement transactions. One click converts them to advances so you can reconcile downstream draw-downs.
              </p>
              {suggestedLoading && suggested.length === 0 ? (
                <p className="text-xs text-muted-foreground text-center py-3">Scanning bank-payments + statements…</p>
              ) : (
                <div className="space-y-2">
                  {groupedSuggestions.map((group) => {
                    const isMulti = group.count > 1;
                    const groupKey = `sugg-${group.key}`;
                    const isExpanded = expandedGroups[groupKey];

                    // Render a single-row group as an inline card (identical
                    // to legacy behaviour). Only collapse when a person
                    // has 2+ suggestions.
                    if (!isMulti) {
                      return renderSuggestionRow(group.rows[0], { indent: false });
                    }

                    return (
                      <div key={groupKey} className="rounded-lg border-2 border-violet-200 bg-white/70 overflow-hidden" data-testid={`sugg-group-${group.key}`}>
                        <button
                          type="button"
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-violet-50/60 transition-colors text-left"
                          onClick={() => toggleGroup(groupKey)}
                          data-testid={`sugg-group-toggle-${group.key}`}
                        >
                          <ChevronRight size={14} className={`text-violet-600 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          <Banknote size={16} className="text-violet-600 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">
                              {group.party_name}
                              <Badge variant="secondary" className="text-[9px] ml-2">{group.count} payments</Badge>
                            </div>
                            <div className="text-[10px] text-muted-foreground mt-0.5">
                              Click to {isExpanded ? 'hide' : 'view'} the {group.count} bank payments
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="text-[10px] text-muted-foreground">Total</div>
                            <div className="font-bold tabular-nums text-rose-700 text-sm">{fmtSR(group.total)}</div>
                          </div>
                        </button>
                        {isExpanded && (
                          <div className="border-t bg-violet-50/30 divide-y divide-violet-100" data-testid={`sugg-group-body-${group.key}`}>
                            {group.rows.map(r => renderSuggestionRow(r, { indent: true }))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Task C — Grouped by Person/Account summary */}
        <Card data-testid="pta-grouped-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setShowGrouped(v => !v)}
                className="flex items-center gap-2 font-bold text-sm hover:text-primary transition-colors"
                data-testid="pta-grouped-toggle"
              >
                {showGrouped ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                Grouped by Person / Account ({grouped.groups?.length || 0})
              </button>
              <div className="text-xs text-muted-foreground tabular-nums flex items-center gap-2">
                <span>
                  Advanced <span className="font-semibold text-foreground">{fmtSR(grouped.total_advance)}</span>
                  {' · '}Adjusted <span className="font-semibold text-foreground">{fmtSR(grouped.total_adjusted)}</span>
                  {' · '}Open remaining <span className="font-semibold text-amber-600">{fmtSR(grouped.open_remaining)}</span>
                </span>
                {(grouped.groups || []).length > 0 && (
                  <button type="button" onClick={() => exportToCsv('pass_through_grouped.csv', grouped.groups, [
                    { key: 'person_name', label: 'Person / Account' },
                    { key: 'person_type', label: 'Type' },
                    { key: 'advances_count', label: 'Count' },
                    { key: 'total_advance', label: 'Total Advance', format: (v) => Number(v || 0).toFixed(2) },
                    { key: 'total_adjusted', label: 'Total Adjusted', format: (v) => Number(v || 0).toFixed(2) },
                    { key: 'remaining_balance', label: 'Remaining', format: (v) => Number(v || 0).toFixed(2) },
                    { key: 'status', label: 'Status' },
                  ])} className="text-info hover:underline font-semibold whitespace-nowrap" data-testid="pta-grouped-export">⬇ CSV</button>
                )}
              </div>
            </div>
            {showGrouped && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="pta-grouped-table">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b">
                      <th className="p-2">Person / Account</th>
                      <th className="p-2 text-center">#</th>
                      <th className="p-2 text-right">Total Advance</th>
                      <th className="p-2 text-right">Total Linked / Adjusted</th>
                      <th className="p-2 text-right">Remaining Balance</th>
                      <th className="p-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(grouped.groups || []).length === 0 ? (
                      <tr><td colSpan={6} className="p-4 text-center text-muted-foreground">No advances yet.</td></tr>
                    ) : grouped.groups.map((g, gi) => {
                      const sc = g.status === 'open' ? 'amber' : g.status === 'over_consumed' ? 'rose' : 'emerald';
                      const isOpen = expandedGroup === (g.person_id || g.person_name);
                      return (
                        <Fragment key={`g-${gi}`}>
                          <tr
                            className="border-b hover:bg-muted/30 cursor-pointer"
                            onClick={() => setExpandedGroup(isOpen ? null : (g.person_id || g.person_name))}
                            data-testid={`pta-group-row-${gi}`}
                          >
                            <td className="p-2 font-semibold flex items-center gap-1.5">
                              {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                              {g.person_name}
                              <Badge variant="outline" className="text-[9px] uppercase">{g.person_type}</Badge>
                            </td>
                            <td className="p-2 text-center tabular-nums text-muted-foreground">{g.advances_count}</td>
                            <td className="p-2 text-right tabular-nums font-medium">{fmtSR(g.total_advance)}</td>
                            <td className="p-2 text-right tabular-nums">{fmtSR(g.total_adjusted)}</td>
                            <td className={`p-2 text-right tabular-nums font-bold text-${sc}-700 dark:text-${sc}-300`}>{fmtSR(g.remaining_balance)}</td>
                            <td className="p-2 text-center">
                              <Badge variant="outline" className={`text-[9px] uppercase bg-${sc}-50 text-${sc}-700 border-${sc}-300`}>
                                {g.status?.replace(/_/g, ' ')}
                              </Badge>
                            </td>
                          </tr>
                          {isOpen && (g.advances || []).map((a, ai) => (
                            <tr key={`g-${gi}-a-${ai}`} className="bg-muted/20 text-[11px]" data-testid={`pta-group-${gi}-adv-${ai}`}>
                              <td className="p-1.5 pl-7 text-muted-foreground">{a.date?.slice(0, 10)} · {a.description || 'No description'}</td>
                              <td></td>
                              <td className="p-1.5 text-right tabular-nums">{fmtSR(a.amount)}</td>
                              <td className="p-1.5 text-right tabular-nums">{fmtSR(a.consumed)}</td>
                              <td className="p-1.5 text-right tabular-nums">{fmtSR(a.remaining)}</td>
                              <td></td>
                            </tr>
                          ))}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        <Tabs value={filter} onValueChange={setFilter}>
          <TabsList>
            <TabsTrigger value="all" data-testid="filter-all">All ({data.items.length})</TabsTrigger>
            <TabsTrigger value="open" data-testid="filter-open">Open ({data.items.filter(i => i.status === 'open').length})</TabsTrigger>
            <TabsTrigger value="fully_consumed" data-testid="filter-fully">Fully consumed ({data.items.filter(i => i.status === 'fully_consumed').length})</TabsTrigger>
            <TabsTrigger value="over_consumed" data-testid="filter-over">Over consumed ({data.items.filter(i => i.status === 'over_consumed').length})</TabsTrigger>
          </TabsList>
          <TabsContent value={filter} className="mt-4 space-y-2">
            {loading ? (
              <p className="text-sm text-muted-foreground p-6 text-center">Loading…</p>
            ) : filtered.length === 0 ? (
              <Card><CardContent className="p-8 text-center text-sm text-muted-foreground">
                No advances {filter === 'all' ? 'yet' : `in status "${filter}"`}.
              </CardContent></Card>
            ) : groupedFiltered.map((group, gi) => {
              const gPct = group.amount > 0 ? Math.max(0, Math.min(100, (group.consumed / group.amount) * 100)) : 0;
              const gColor = group.status === 'open' ? 'amber'
                : group.status === 'over_consumed' ? 'rose' : 'emerald';
              const isMulti = group.count > 1;
              const isExpanded = expandedGroups[group.key];

              // Single-advance persons render as the classic single card
              // (no group chrome) so the UI doesn't feel over-engineered
              // for the majority case.
              if (!isMulti) {
                const adv = group.advances[0];
                const pct = adv.amount > 0 ? Math.max(0, Math.min(100, (adv.consumed / adv.amount) * 100)) : 0;
                const stateColor = adv.status === 'open' ? 'amber'
                  : adv.status === 'over_consumed' ? 'rose' : 'emerald';
                return (
                  <Card
                    key={adv.id || gi}
                    className={`border-l-4 border-l-${stateColor}-400 hover:shadow-sm transition-shadow cursor-pointer`}
                    onClick={() => openDetail(adv)}
                    data-testid={`advance-row-${gi}`}
                  >
                    <CardContent className="p-4 flex items-center gap-4 flex-wrap">
                      <div className="flex-1 min-w-[180px]">
                        <div className="font-bold text-sm flex items-center gap-2">
                          {adv.person_name || '—'}
                          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
                            {adv.person_type}
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {adv.date?.slice(0, 10)} · {adv.description || 'No description'}
                        </div>
                      </div>
                      <div className="text-right min-w-[120px]">
                        <div className="text-xs text-muted-foreground">Advanced</div>
                        <div className="font-bold tabular-nums">{fmtSR(adv.amount)}</div>
                      </div>
                      <div className="text-right min-w-[120px]">
                        <div className="text-xs text-muted-foreground">Consumed</div>
                        <div className={`font-bold tabular-nums text-${stateColor}-700 dark:text-${stateColor}-300`}>{fmtSR(adv.consumed)}</div>
                      </div>
                      <div className="text-right min-w-[120px]">
                        <div className="text-xs text-muted-foreground">Remaining</div>
                        <div className="font-bold tabular-nums">{fmtSR(adv.remaining)}</div>
                      </div>
                      <div className="w-32 h-2 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                        <div className={`h-full bg-${stateColor}-500 transition-all`} style={{ width: `${pct}%` }} />
                      </div>
                      <Badge variant="outline" className={`text-[9px] uppercase tracking-wider bg-${stateColor}-50 text-${stateColor}-700 border-${stateColor}-300`}>
                        {adv.status === 'open' && <AlertTriangle size={9} className="mr-0.5" />}
                        {adv.status === 'fully_consumed' && <CheckCircle2 size={9} className="mr-0.5" />}
                        {adv.status?.replace(/_/g, ' ')}
                      </Badge>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-blue-600" onClick={(e) => { e.stopPropagation(); openExpenseFor(adv); }} data-testid={`advance-record-expense-${gi}`} title="Record an expense drawn from this advance">
                        <Receipt size={11} className="mr-1" /> Expense
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px]" onClick={(e) => { e.stopPropagation(); openDetail(adv); }} data-testid={`advance-view-${gi}`}>
                        <Eye size={11} />
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-[10px] text-rose-600" onClick={(e) => { e.stopPropagation(); onDelete(adv); }} data-testid={`advance-delete-${gi}`}>
                        <Trash2 size={11} />
                      </Button>
                    </CardContent>
                  </Card>
                );
              }

              // Multi-advance group: collapsible summary + child rows.
              return (
                <Card key={group.key} className={`border-l-4 border-l-${gColor}-400`} data-testid={`advance-group-${gi}`}>
                  <CardContent
                    className="p-4 flex items-center gap-4 flex-wrap cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => toggleGroup(group.key)}
                    data-testid={`advance-group-toggle-${gi}`}
                  >
                    <div className="flex-1 min-w-[180px] flex items-center gap-2">
                      <ChevronRight size={16} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <div>
                        <div className="font-bold text-sm flex items-center gap-2">
                          {group.person_name}
                          <Badge variant="outline" className="text-[9px] uppercase tracking-wider">
                            {group.person_type}
                          </Badge>
                          <Badge variant="secondary" className="text-[9px]">
                            {group.count} advances
                          </Badge>
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          Click to {isExpanded ? 'hide' : 'view'} the {group.count} individual advances
                        </div>
                      </div>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <div className="text-xs text-muted-foreground">Total Advanced</div>
                      <div className="font-bold tabular-nums">{fmtSR(group.amount)}</div>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <div className="text-xs text-muted-foreground">Consumed</div>
                      <div className={`font-bold tabular-nums text-${gColor}-700 dark:text-${gColor}-300`}>{fmtSR(group.consumed)}</div>
                    </div>
                    <div className="text-right min-w-[120px]">
                      <div className="text-xs text-muted-foreground">Remaining</div>
                      <div className="font-bold tabular-nums">{fmtSR(group.remaining)}</div>
                    </div>
                    <div className="w-32 h-2 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                      <div className={`h-full bg-${gColor}-500 transition-all`} style={{ width: `${gPct}%` }} />
                    </div>
                    <Badge variant="outline" className={`text-[9px] uppercase tracking-wider bg-${gColor}-50 text-${gColor}-700 border-${gColor}-300`}>
                      {group.status?.replace(/_/g, ' ')}
                    </Badge>
                  </CardContent>

                  {isExpanded && (
                    <div className="border-t bg-muted/10 p-3 space-y-2" data-testid={`advance-group-body-${gi}`}>
                      {group.advances.map((adv, ci) => {
                        const pct = adv.amount > 0 ? Math.max(0, Math.min(100, (adv.consumed / adv.amount) * 100)) : 0;
                        const stateColor = adv.status === 'open' ? 'amber'
                          : adv.status === 'over_consumed' ? 'rose' : 'emerald';
                        return (
                          <div
                            key={adv.id || ci}
                            className={`ml-6 pl-3 border-l-2 border-${stateColor}-300 flex items-center gap-3 flex-wrap py-2 rounded hover:bg-background cursor-pointer`}
                            onClick={() => openDetail(adv)}
                            data-testid={`advance-child-${gi}-${ci}`}
                          >
                            <div className="flex-1 min-w-[180px]">
                              <div className="text-xs font-medium">
                                {adv.date?.slice(0, 10)} · {adv.description || 'No description'}
                              </div>
                              <div className="text-[10px] text-muted-foreground mt-0.5">
                                Ref: {adv.reference || adv.id?.slice(0, 8)}
                              </div>
                            </div>
                            <div className="text-right min-w-[90px]">
                              <div className="text-[10px] text-muted-foreground">Amt</div>
                              <div className="text-xs font-bold tabular-nums">{fmtSR(adv.amount)}</div>
                            </div>
                            <div className="text-right min-w-[90px]">
                              <div className="text-[10px] text-muted-foreground">Used</div>
                              <div className={`text-xs font-bold tabular-nums text-${stateColor}-700`}>{fmtSR(adv.consumed)}</div>
                            </div>
                            <div className="text-right min-w-[90px]">
                              <div className="text-[10px] text-muted-foreground">Left</div>
                              <div className="text-xs font-bold tabular-nums">{fmtSR(adv.remaining)}</div>
                            </div>
                            <div className="w-24 h-1.5 bg-stone-200 dark:bg-stone-800 rounded-full overflow-hidden">
                              <div className={`h-full bg-${stateColor}-500`} style={{ width: `${pct}%` }} />
                            </div>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[9px] text-blue-600" onClick={(e) => { e.stopPropagation(); openExpenseFor(adv); }} data-testid={`advance-child-expense-${gi}-${ci}`}>
                              <Receipt size={10} className="mr-1" /> Expense
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[9px]" onClick={(e) => { e.stopPropagation(); openDetail(adv); }}>
                              <Eye size={10} />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-6 px-2 text-[9px] text-rose-600" onClick={(e) => { e.stopPropagation(); onDelete(adv); }}>
                              <Trash2 size={10} />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </Card>
              );
            })}
          </TabsContent>
        </Tabs>

        {/* Create dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="sm:max-w-lg" data-testid="advance-create-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><HandCoins size={16} /> New Advance</DialogTitle>
              <DialogDescription>Money you transfer to an employee or partner for company spend.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Person Type</Label>
                  <Select value={form.person_type} onValueChange={(v) => setForm(f => ({ ...f, person_type: v, person_id: '' }))}>
                    <SelectTrigger data-testid="adv-person-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="employee">Employee</SelectItem>
                      <SelectItem value="partner">Partner</SelectItem>
                      <SelectItem value="supplier">Supplier (prepayment)</SelectItem>
                      <SelectItem value="customer">Customer (overpayment credit)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Person *</Label>
                  <Select value={form.person_id} onValueChange={(v) => setForm(f => ({ ...f, person_id: v }))}>
                    <SelectTrigger data-testid="adv-person-id"><SelectValue placeholder="Pick..." /></SelectTrigger>
                    <SelectContent>
                      {personOptions.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount *</Label>
                  <Input type="number" step="0.01" value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))} data-testid="adv-amount" />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Mode</Label>
                  <Select value={form.payment_mode} onValueChange={v => setForm(f => ({ ...f, payment_mode: v }))}>
                    <SelectTrigger data-testid="adv-mode"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Paying Branch</Label>
                  <Select value={form.paying_branch_id} onValueChange={v => setForm(f => ({ ...f, paying_branch_id: v }))}>
                    <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {form.payment_mode === 'bank' && (
                <div>
                  <Label>Bank Account *</Label>
                  <Select value={form.paying_bank_account_id} onValueChange={v => setForm(f => ({ ...f, paying_bank_account_id: v }))}>
                    <SelectTrigger data-testid="adv-bank-account"><SelectValue placeholder="Pick bank account..." /></SelectTrigger>
                    <SelectContent>
                      {bankAccounts.map(b => <SelectItem key={b.id} value={b.id}>{b.name || b.account_name || b.bank_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Input placeholder="e.g. Purchase advance for Q2" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
              </div>
              <div>
                <Label>Reference</Label>
                <Input placeholder="TRX-..." value={form.reference} onChange={e => setForm(f => ({ ...f, reference: e.target.value }))} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={submit} disabled={submitting} data-testid="adv-submit">
                {submitting ? 'Recording…' : 'Record Advance'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Detail dialog */}
        <Dialog open={!!detailFor} onOpenChange={(o) => !o && setDetailFor(null)}>
          <DialogContent className="sm:max-w-2xl max-h-[88dvh] overflow-y-auto" data-testid="advance-detail-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HandCoins size={16} /> {detailFor?.person_name} · {fmtSR(detailFor?.amount)}
              </DialogTitle>
              <DialogDescription>
                {detailFor?.date?.slice(0, 10)} · {detailFor?.description || 'No description'}
              </DialogDescription>
            </DialogHeader>
            {detailFor && (
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Advanced</div>
                    <div className="font-bold mt-1 tabular-nums">{fmtSR(detailFor.amount)}</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Consumed</div>
                    <div className="font-bold mt-1 tabular-nums">{fmtSR(detailFor.consumed)}</div>
                  </CardContent></Card>
                  <Card><CardContent className="p-3 text-center">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Remaining</div>
                    <div className={`font-bold mt-1 tabular-nums ${detailFor.remaining > 0.5 ? 'text-amber-600' : detailFor.remaining < -0.5 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      {fmtSR(detailFor.remaining)}
                    </div>
                  </CardContent></Card>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Linked Expenses ({detailFor.linked_expenses?.length || 0})
                  </h4>
                  {(!detailFor.linked_expenses || detailFor.linked_expenses.length === 0) ? (
                    <p className="text-xs text-muted-foreground p-2">No expenses linked yet. Tag any expense with `funded_by_advance_id={detailFor.id}` to draw it down.</p>
                  ) : detailFor.linked_expenses.map((e, i) => (
                    <div key={e.id || i} className="flex justify-between items-center py-1.5 px-2 border-b text-xs hover:bg-stone-50 dark:hover:bg-stone-800/50">
                      <span>{e.date?.slice(0, 10)} · {e.category} · {e.description?.slice(0, 50)}</span>
                      <span className="font-bold tabular-nums">{fmtSR(e.amount)}</span>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                    Linked Supplier Payments ({detailFor.linked_supplier_payments?.length || 0})
                  </h4>
                  {(!detailFor.linked_supplier_payments || detailFor.linked_supplier_payments.length === 0) ? (
                    <p className="text-xs text-muted-foreground p-2">None</p>
                  ) : detailFor.linked_supplier_payments.map((p, i) => (
                    <div key={p.id || i} className="flex justify-between items-center py-1.5 px-2 border-b text-xs hover:bg-stone-50 dark:hover:bg-stone-800/50">
                      <span>{p.date?.slice(0, 10)} · {p.notes?.slice(0, 50)}</span>
                      <span className="font-bold tabular-nums">{fmtSR(p.amount)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Inline "+ Record expense" dialog. Creates an expense row
            funded_by_advance_id = expenseAdvance.id so the advance's
            consumed/remaining balance updates on the next fetch. */}
        <Dialog open={!!expenseAdvance} onOpenChange={(v) => !v && setExpenseAdvance(null)}>
          <DialogContent className="sm:max-w-md" data-testid="record-expense-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt size={16} /> Record draw-down from advance
              </DialogTitle>
              {expenseAdvance && (
                <DialogDescription>
                  From <strong>{expenseAdvance.person_name}</strong>&apos;s advance.
                  Remaining: <span className="font-semibold">{fmtSR(expenseAdvance.remaining)}</span>
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Type *</Label>
                <Select
                  value={expenseForm.kind}
                  onValueChange={(v) => setExpenseForm(f => ({ ...f, kind: v }))}
                >
                  <SelectTrigger data-testid="downstream-kind-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {KIND_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value} data-testid={`kind-opt-${o.value}`}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Per-kind specific fields */}
              {expenseForm.kind === 'expense' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category *</Label>
                    <Input
                      value={expenseForm.category}
                      onChange={(e) => setExpenseForm(f => ({ ...f, category: e.target.value }))}
                      placeholder="e.g. Fuel, Supplies"
                      data-testid="expense-category"
                    />
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={expenseForm.vendor}
                      onChange={(e) => setExpenseForm(f => ({ ...f, vendor: e.target.value }))}
                      placeholder="Who you paid"
                      data-testid="expense-vendor"
                    />
                  </div>
                </div>
              )}

              {expenseForm.kind === 'supplier_payment' && (
                <div>
                  <Label>Supplier *</Label>
                  <Select
                    value={expenseForm.supplier_id}
                    onValueChange={(v) => {
                      const sup = (people.suppliers || []).find(s => s.id === v);
                      setExpenseForm(f => ({ ...f, supplier_id: v, supplier_name: sup?.name || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="supplier-select"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                    <SelectContent>
                      {(people.suppliers || []).map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {expenseForm.kind === 'salary_advance' && (
                <div>
                  <Label>Employee *</Label>
                  <Select
                    value={expenseForm.employee_id}
                    onValueChange={(v) => {
                      const emp = (people.employees || []).find(e => e.id === v);
                      setExpenseForm(f => ({ ...f, employee_id: v, employee_name: emp?.full_name || emp?.name || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="employee-select"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                    <SelectContent>
                      {(people.employees || []).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.full_name || e.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {/* iter921 — Payment Type switch: Salary posts to the
                      Employee Profile with a Salary Month; Advance keeps the
                      legacy draw-down behaviour. */}
                  <div className="mt-3">
                    <Label>Payment Type</Label>
                    <div className="flex items-center gap-1 mt-1 bg-muted rounded-lg p-1 w-fit" data-testid="pta-payment-type">
                      {[['advance', 'Advance'], ['salary', 'Salary']].map(([val, lbl]) => (
                        <button
                          key={val}
                          type="button"
                          onClick={() => setExpenseForm(f => ({ ...f, payment_type: val }))}
                          className={`text-xs font-semibold px-3 py-1 rounded-md transition ${expenseForm.payment_type === val ? 'bg-emerald-600 text-white' : 'text-muted-foreground hover:text-foreground'}`}
                          data-testid={`pta-payment-type-${val}`}
                        >{lbl}</button>
                      ))}
                    </div>
                  </div>
                  {expenseForm.payment_type === 'salary' && (
                    <div className="mt-3">
                      <Label>Salary Month</Label>
                      <Input
                        type="month"
                        value={expenseForm.salary_month}
                        onChange={(e) => setExpenseForm(f => ({ ...f, salary_month: e.target.value }))}
                        data-testid="pta-salary-month"
                        className="mt-1"
                      />
                      <p className="text-[11px] text-muted-foreground mt-1">Posts as a salary payment for this month — visible in the Employee Profile.</p>
                    </div>
                  )}
                </div>
              )}

              {expenseForm.kind === 'partner_payment' && (
                <div>
                  <Label>Partner *</Label>
                  <Select
                    value={expenseForm.partner_id}
                    onValueChange={(v) => {
                      const p = (people.partners || []).find(x => x.id === v);
                      setExpenseForm(f => ({ ...f, partner_id: v, partner_name: p?.name || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="partner-select"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                    <SelectContent>
                      {(people.partners || []).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {expenseForm.kind === 'loan_repayment' && (
                <div>
                  <Label>Lender / Loan ID</Label>
                  <Input
                    value={expenseForm.lender_name}
                    onChange={(e) => setExpenseForm(f => ({ ...f, lender_name: e.target.value }))}
                    placeholder="Lender / loan reference"
                    data-testid="lender-name"
                  />
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Amount (SR) *</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={expenseForm.amount}
                    onChange={(e) => setExpenseForm(f => ({ ...f, amount: e.target.value }))}
                    data-testid="expense-amount"
                  />
                </div>
                <div>
                  <Label>Date</Label>
                  <Input
                    type="date"
                    value={expenseForm.date}
                    onChange={(e) => setExpenseForm(f => ({ ...f, date: e.target.value }))}
                  />
                </div>
              </div>

              {/* iter824 — branch selector. Defaults to the advance's paying
                  branch but overridable so a draw-down can be posted against
                  a different branch's P&L when the person actually spends
                  the money at another location. */}
              <div>
                <Label>Branch *</Label>
                <Select
                  value={expenseForm.branch_id}
                  onValueChange={(v) => setExpenseForm(f => ({ ...f, branch_id: v }))}
                >
                  <SelectTrigger data-testid="expense-branch-select">
                    <SelectValue placeholder="Pick a branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Description</Label>
                <Input
                  value={expenseForm.description}
                  onChange={(e) => setExpenseForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setExpenseAdvance(null)} disabled={expenseSubmitting}>
                Cancel
              </Button>
              <Button onClick={submitExpense} disabled={expenseSubmitting} data-testid="expense-submit" className="bg-blue-600 hover:bg-blue-700">
                {expenseSubmitting ? 'Saving…' : `Record ${KIND_LABELS[expenseForm.kind]}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* iter500 — Direct-Convert dialog (Expense / Supplier / Salary
            / Partner from a bank-statement suggestion without going via
            an Advance). Submits to /pass-through-advances/from-
            suggestion-direct which stamps the txn as linked. */}
        <Dialog open={directConvertOpen} onOpenChange={setDirectConvertOpen}>
          <DialogContent className="sm:max-w-md" data-testid="direct-convert-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt size={16} /> Convert to {KIND_LABELS[directConvertKind]}
              </DialogTitle>
              {directConvertRow && (
                <DialogDescription>
                  From bank: <strong>{directConvertRow.party_name || directConvertRow.raw_party_name || '—'}</strong>
                  · {fmtSR(directConvertRow.amount)} on {(directConvertRow.date || '').slice(0, 10)}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              {directConvertKind === 'expense' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category *</Label>
                    <Input
                      value={directConvertForm.category}
                      onChange={(e) => setDirectConvertForm(f => ({ ...f, category: e.target.value }))}
                      placeholder="e.g. Utilities, Fuel"
                      data-testid="direct-expense-category"
                    />
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={directConvertForm.vendor}
                      onChange={(e) => setDirectConvertForm(f => ({ ...f, vendor: e.target.value }))}
                      data-testid="direct-expense-vendor"
                    />
                  </div>
                </div>
              )}
              {directConvertKind === 'supplier_payment' && (
                <div>
                  <Label>Supplier *</Label>
                  <Select
                    value={directConvertForm.supplier_id}
                    onValueChange={(v) => {
                      const sup = (people.suppliers || []).find(s => s.id === v);
                      setDirectConvertForm(f => ({ ...f, supplier_id: v, supplier_name: sup?.label || sup?.name || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="direct-supplier-select"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                    <SelectContent>
                      {(people.suppliers || []).map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.label || s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {directConvertKind === 'salary_advance' && (
                <div>
                  <Label>Employee *</Label>
                  <Select
                    value={directConvertForm.employee_id}
                    onValueChange={(v) => {
                      const emp = (people.employees || []).find(e => e.id === v);
                      setDirectConvertForm(f => ({ ...f, employee_id: v, employee_name: emp?.label || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="direct-employee-select"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                    <SelectContent>
                      {(people.employees || []).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {directConvertKind === 'partner_payment' && (
                <div>
                  <Label>Partner *</Label>
                  <Select
                    value={directConvertForm.partner_id}
                    onValueChange={(v) => {
                      const p = (people.partners || []).find(x => x.id === v);
                      setDirectConvertForm(f => ({ ...f, partner_id: v, partner_name: p?.label || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="direct-partner-select"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                    <SelectContent>
                      {(people.partners || []).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Description</Label>
                <Input
                  value={directConvertForm.description}
                  onChange={(e) => setDirectConvertForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Optional notes"
                />
              </div>
              <div>
                <Label>Date</Label>
                <Input
                  type="date"
                  value={directConvertForm.date}
                  onChange={(e) => setDirectConvertForm(f => ({ ...f, date: e.target.value }))}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDirectConvertOpen(false)} disabled={directConvertSubmitting}>
                Cancel
              </Button>
              <Button onClick={submitDirectConvert} disabled={directConvertSubmitting} data-testid="direct-convert-submit" className="bg-blue-600 hover:bg-blue-700">
                {directConvertSubmitting ? 'Saving…' : `Record ${KIND_LABELS[directConvertKind]}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        {/* iter501 — Bulk Convert dialog. Lets user preview every
            "similar" un-linked bank row (by description signature OR
            same resolved person) and convert them all to the same
            target kind in one batch. */}
        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
          <DialogContent className="sm:max-w-2xl max-h-[90dvh] overflow-y-auto" data-testid="bulk-convert-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-600" /> Bulk Convert Similar Rows
              </DialogTitle>
              {bulkAnchor && (
                <DialogDescription>
                  Anchor: <strong>{bulkAnchor.party_name || bulkAnchor.raw_party_name || '—'}</strong>
                  · {fmtSR(bulkAnchor.amount)} · {(bulkAnchor.description || '').slice(0, 80)}
                </DialogDescription>
              )}
            </DialogHeader>
            <div className="space-y-3">
              {/* Match strategy */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Match strategy</Label>
                  <Select value={bulkMatchBy} onValueChange={onBulkMatchByChange}>
                    <SelectTrigger data-testid="bulk-match-by"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="signature" data-testid="bulk-match-signature">By description (first 2 words)</SelectItem>
                      <SelectItem
                        value="person"
                        disabled={!(bulkAnchor?.payee_id && (bulkAnchor.kind === 'employee' || bulkAnchor.kind === 'partner'))}
                        data-testid="bulk-match-person"
                      >By same person</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target kind</Label>
                  <Select value={bulkKind} onValueChange={setBulkKind}>
                    <SelectTrigger data-testid="bulk-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="expense">Expense</SelectItem>
                      <SelectItem value="supplier_payment">Supplier Payment</SelectItem>
                      <SelectItem value="salary_advance">Salary / Employee Advance</SelectItem>
                      <SelectItem value="partner_payment">Partner Draw</SelectItem>
                      <SelectItem value="advance">Pass-Through Advance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview banner */}
              <div className="rounded-lg border border-amber-200 bg-amber-50/70 dark:bg-amber-950/20 p-3" data-testid="bulk-preview-banner">
                {bulkPreview.loading ? (
                  <p className="text-xs text-muted-foreground">Searching for similar rows…</p>
                ) : (
                  <>
                    <p className="text-sm">
                      Found <strong className="text-amber-700" data-testid="bulk-preview-count">{bulkPreview.count}</strong> similar un-linked row{bulkPreview.count === 1 ? '' : 's'}
                      {bulkMatchBy === 'signature' && bulkPreview.anchor_signature ? <> matching <code className="text-[10px] bg-white px-1 rounded">{bulkPreview.anchor_signature}</code></> : null}
                      {' · '}total <strong className="tabular-nums">{fmtSR(bulkPreview.total_amount)}</strong>
                    </p>
                    {bulkPreview.samples.length > 0 && (
                      <div className="mt-2 max-h-32 overflow-y-auto text-[11px] space-y-0.5 border-t border-amber-200 pt-1.5">
                        {bulkPreview.samples.slice(0, 8).map((s, i) => (
                          <div key={i} className="flex justify-between gap-2" data-testid={`bulk-preview-sample-${i}`}>
                            <span className="truncate">{(s.date || '').slice(0, 10)} · {s.description}</span>
                            <span className="tabular-nums shrink-0">{fmtSR(s.amount)}</span>
                          </div>
                        ))}
                        {bulkPreview.count > 8 && (
                          <p className="text-muted-foreground italic">…and {bulkPreview.count - 8} more</p>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Per-kind required fields (applied to every match) */}
              {bulkKind === 'expense' && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Category *</Label>
                    <Input
                      value={bulkForm.category}
                      onChange={(e) => setBulkForm(f => ({ ...f, category: e.target.value }))}
                      placeholder="e.g. Utilities, Fuel, Bank Fees"
                      data-testid="bulk-category"
                    />
                  </div>
                  <div>
                    <Label>Vendor (default)</Label>
                    <Input
                      value={bulkForm.vendor}
                      onChange={(e) => setBulkForm(f => ({ ...f, vendor: e.target.value }))}
                      data-testid="bulk-vendor"
                    />
                  </div>
                </div>
              )}
              {bulkKind === 'supplier_payment' && (
                <div>
                  <Label>Supplier *</Label>
                  <Select
                    value={bulkForm.supplier_id}
                    onValueChange={(v) => {
                      const sup = (people.suppliers || []).find(s => s.id === v);
                      setBulkForm(f => ({ ...f, supplier_id: v, supplier_name: sup?.label || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="bulk-supplier-select"><SelectValue placeholder="Pick a supplier" /></SelectTrigger>
                    <SelectContent>
                      {(people.suppliers || []).map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {bulkKind === 'salary_advance' && bulkMatchBy === 'signature' && (
                <div>
                  <Label>Employee (applied to every match) *</Label>
                  <Select
                    value={bulkForm.employee_id}
                    onValueChange={(v) => {
                      const emp = (people.employees || []).find(e => e.id === v);
                      setBulkForm(f => ({ ...f, employee_id: v, employee_name: emp?.label || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="bulk-employee-select"><SelectValue placeholder="Pick an employee" /></SelectTrigger>
                    <SelectContent>
                      {(people.employees || []).map(e => (
                        <SelectItem key={e.id} value={e.id}>{e.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {bulkKind === 'partner_payment' && bulkMatchBy === 'signature' && (
                <div>
                  <Label>Partner (applied to every match) *</Label>
                  <Select
                    value={bulkForm.partner_id}
                    onValueChange={(v) => {
                      const p = (people.partners || []).find(x => x.id === v);
                      setBulkForm(f => ({ ...f, partner_id: v, partner_name: p?.label || '' }));
                    }}
                  >
                    <SelectTrigger data-testid="bulk-partner-select"><SelectValue placeholder="Pick a partner" /></SelectTrigger>
                    <SelectContent>
                      {(people.partners || []).map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                <input
                  type="checkbox"
                  id="bulk-include-anchor"
                  checked={bulkForm.include_anchor}
                  onChange={(e) => setBulkForm(f => ({ ...f, include_anchor: e.target.checked }))}
                  data-testid="bulk-include-anchor"
                />
                <label htmlFor="bulk-include-anchor">Also convert the anchor row (otherwise only the {bulkPreview.count} similar rows are converted)</label>
              </div>
            </div>
            <DialogFooter>
              <div className="flex items-center gap-2 mr-auto w-full sm:w-auto">
                <Input
                  placeholder="Save as preset (optional name)"
                  value={presetSaveName}
                  onChange={(e) => setPresetSaveName(e.target.value)}
                  className="h-8 text-xs flex-1 sm:w-44"
                  data-testid="bulk-preset-name"
                />
                <Button
                  variant="outline"
                  size="sm"
                  className="h-8 text-[10px] border-emerald-300 text-emerald-700"
                  disabled={!presetSaveName.trim() || bulkSubmitting}
                  onClick={savePresetFromBulk}
                  data-testid="bulk-save-preset"
                  title="Save this config so you can re-run it on future statements"
                >
                  <Sparkles size={10} className="mr-1" /> Save Preset
                </Button>
              </div>
              <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={submitBulkConvert}
                disabled={bulkSubmitting || (bulkPreview.count === 0 && !bulkForm.include_anchor)}
                data-testid="bulk-convert-submit"
                className="bg-amber-600 hover:bg-amber-700"
              >
                {bulkSubmitting ? 'Converting…' : `Convert ${bulkPreview.count + (bulkForm.include_anchor ? 1 : 0)} row${(bulkPreview.count + (bulkForm.include_anchor ? 1 : 0)) === 1 ? '' : 's'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
